import React, { useState, useReducer, useContext } from 'react'
import styled from 'styled-components'

import { Column, useTable } from 'react-table'

import { LargeCurrencyLogo } from './CurrencyLogo'
import PoolColumn from './PoolColumn'
import PercentageValue from './PercentageValue'
import CurrencyValue from './CurrencyValue'
import { SwaapLargeLoader, SimpleLoader } from './catalog/SwaapLoader'
import { coinImage } from '../assets/coins/coins'
import { TokenWithData } from '../api/api-types'
import { PartialLine } from '../logic/pools/process-pools'
import { PoolPanelStyle } from './pools/PoolPanelStyle'
import { Rewards } from './catalog/Rewards'
import { NetAPR } from './catalog/MouseoverLegend'

import {
  getButtonsPerPool,
  PoolTableButton,
  TableLine,
} from './pools/pool-table'
import {
  PoolComposition,
  PoolCompositions,
  PoolCompositionSymbol,
  PoolCompositionWeight,
  PoolCompositionLogoSymbol,
} from './pools/PoolComposition'
import { PoolButton } from './pools/PoolButton'
import { SimpleGrid } from './catalog/SimpleGrid'
import { DepositPopup } from './pools/deposit/DepositPopup'
import { usePools } from '../hooks/use-pools'
import { useReloader } from '../hooks/use-reload'
import { WithdrawPopup } from './pools/withdraw/WithdrawPopup'
import { calculateBagInDollar } from '../logic/financials/calculate-bag-in-dollar'
import { Web3Context } from '../hooks/web3'
import { EmptyCell } from './catalog/EmptyCell'
import { showBool } from './catalog/show-bool'
import { useNavigate } from 'react-router-dom'
import { createEarnTabEvent } from 'user-events-segment'
import { div } from '../logic/weis/wei-calculate'

const CellWrapper = styled.div`
  margin-top: 15px;
`

interface TableProps {
  columns: Column<TableLine>[]
  data: TableLine[]
}

function PoolPanelTable({ columns, data }: TableProps) {
  // Use the state and functions returned from useTable to build your UI
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data,
    })

  // Render the UI for your table
  return (
    <table {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, i) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) => (
              <th {...column.getHeaderProps()}>{column.render('Header')}</th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
interface HasValue<T> {
  value: T
}

interface Props {
  enableUser: boolean
  connectNonce: number | undefined
}
function PoolPanel({ enableUser }: Props) {
  // Force update needed for pool calculation, because of usePool
  // https://stackoverflow.com/a/59602379/968988
  const [updater, forceUpdate] = useReducer(x => x + 1, 0)
  const navigate = useNavigate()

  const [showWithdrawPanel, setShowWithdrawPanel] = useState(false)
  const [showDepositPanel, setShowDepositPanel] = useState(false)
  const [selectedPool, setSelectedPool] = useState<PartialLine>()
  const { pools, loading, userSharesLoaded } = usePools()
  const { nonce, connected } = useReloader()

  const { isConnectedToWeb3, web3, account } = useContext(Web3Context)

  enableUser = enableUser && isConnectedToWeb3

  const isLoaded = !loading
  // Call hook passing in the ref and a function to call on outside click
  const onExit = () => {
    setShowWithdrawPanel(false)
    setShowDepositPanel(false)
  }

  const columns = React.useMemo((): Column<TableLine>[] => {
    // @ts-ignore
    return [
      {
        Header: 'Composition',
        accessor: 'composition',
        Cell: (e: any) => {
          return (
            <PoolCompositions>
              {e.value.map((token: TokenWithData, index: number) => {
                return (
                  <PoolComposition key={`pool-composition-${index}`}>
                    <PoolCompositionLogoSymbol key={`logo-symbol-${index}`}>
                      <LargeCurrencyLogo
                        key={`logo-${index}`}
                        src={coinImage(token['symbol'])!}
                      />
                      <PoolCompositionSymbol
                        key={`pool-composition-symbol-${index}`}
                      >
                        {token['symbol']}
                      </PoolCompositionSymbol>
                    </PoolCompositionLogoSymbol>
                    <PoolCompositionWeight
                      key={`pool-composition-weight-${index}`}
                    >
                      {Math.round(token.percent)}%
                    </PoolCompositionWeight>
                  </PoolComposition>
                )
              })}
            </PoolCompositions>
          )
        },
      },
      {
        Header: 'My invest',
        accessor: 'userValue',
        Cell: (e: any) => {
          if (!enableUser) {
            return (
              <CellWrapper>
                <EmptyCell />
              </CellWrapper>
            )
          }
          return userSharesLoaded ? (
            <CellWrapper>
              <CurrencyValue
                precision={3}
                currency="$"
                value={e.value || '0'}
              />
            </CellWrapper>
          ) : isConnectedToWeb3 ? (
            <SimpleLoader />
          ) : (
            <div></div>
          )
        },
      },
      {
        Header: 'TVL',
        accessor: 'tvl',
        Cell: (e: any) => {
          return (
            <CellWrapper>
              <CurrencyValue
                precision={3}
                currency="$"
                value={e.value ? e.value : 0}
              />
            </CellWrapper>
          )
        },
      },
      //   Header: `Volume (24h)`,
      //   accessor: 'dailyVolume',
      //   Cell: (e: any) => {
      //     return e.value == undefined ? (
      //       <span>
      //         {' '}
      //         <SimpleLoader />{' '}
      //       </span>
      //     ) : (
      //       <CellWrapper>
      //         <CurrencyValue precision={3} currency="$" value={e.value} />
      //       </CellWrapper>
      //     )
      //   },
      // },
      {
        Header: (
          <span
            onMouseOver={() => {
              createEarnTabEvent().hover_net_apr_tooltip('start')
            }}
            onMouseOut={() => {
              createEarnTabEvent().hover_net_apr_tooltip('end')
            }}
          >
            <NetAPR>
              <span>
                Net APR{' '}
                <span style={{ fontSize: '1rem', padding: '0px 5px 0px 1px' }}>
                  (since inception)
                </span>
              </span>
            </NetAPR>
          </span>
        ),
        accessor: 'apr',
        Cell: (e: any) => {
          return e.value == undefined ? (
            <span>
              <SimpleLoader />
            </span>
          ) : (
            <CellWrapper>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <PercentageValue value={e.value} />
                <div
                  onMouseOver={() => {
                    createEarnTabEvent(getLine(e).pool).hover_swaap_coins_tip(
                      'start'
                    )
                  }}
                  onMouseOut={() => {
                    createEarnTabEvent(getLine(e).pool).hover_swaap_coins_tip(
                      'end'
                    )
                  }}
                >
                  <Rewards />
                </div>
              </div>
            </CellWrapper>
          )
        },
      },
      {
        Header: 'Action',
        accessor: 'buttons',
        Cell: (e: HasValue<Array<PoolTableButton>>) => {
          const buttons = e.value.map((value, i) => (
            <PoolButton
              key={i}
              onClick={() => {
                value.message.toLowerCase().includes('deposit')
                  ? createEarnTabEvent(getLine(e).pool).clicked_deposit()
                  : createEarnTabEvent(getLine(e).pool).clicked_withdraw()
                value.action()
              }}
            >
              {value.message}
            </PoolButton>
          ))

          return enableUser ? (
            <SimpleGrid gap={'small'}>{buttons}</SimpleGrid>
          ) : (
            <CellWrapper>
              <EmptyCell />
            </CellWrapper>
          )
        },
      },
    ]
  }, [pools, updater])

  return (
    <PoolPanelStyle isLoaded={isLoaded}>
      {
        <div style={{ alignSelf: 'center' }}>
          {isLoaded ? (
            <div style={{ alignSelf: 'center' }}>
              {pools.length > 0 ? (
                <>
                  <PoolPanelTable
                    columns={columns}
                    data={pools.map((line, index) => {
                      return {
                        buttons: getButtonsPerPool(
                          line,
                          () => {
                            navigate(`/pools/${line.pool.address}/deposit`)
                          },
                          () => {
                            navigate(`/pools/${line.pool.address}/withdraw`)
                          }
                        ),
                        pool: line.pool,
                        composition: line.tokens,
                        myTVL: line.userValue,
                        tvl: line.tvl,
                        dailyVolume: line.dailyVolume,
                        apr: line.net_apr_since_inception,
                        userValue: line.userValue,
                        userShares: line.userShares,
                        tokens: line.tokens,
                        net_apr_since_inception: line.net_apr_since_inception,
                      }
                    })}
                  />
                </>
              ) : (
                <PoolColumn>No pool available.</PoolColumn>
              )}
            </div>
          ) : (
            <SwaapLargeLoader />
          )}
        </div>
      }
    </PoolPanelStyle>
  )
}

export default PoolPanel

export const MemoPoolPanel = React.memo(
  PoolPanel,
  (props: Props, nextProps: Props) => {
    return props.connectNonce === nextProps.connectNonce
  }
)

function getLine(e: any): any {
  return e.data[e.row.index]
}
